/**
 * Live assigned-chat unread totals + per-user counts (CSR / Sales).
 * Single socket listener; sidebar + list pages subscribe here.
 */
import csrOutreachService from "../api/services/csrOutreachService";
import salesService from "../api/services/salesService";
import { getSupportSocket } from "../api/services/supportSocket";
import { isAdminChatOpen } from "./openAdminChatIds";

const state = {
  csr: { total: 0, byUserId: {} },
  sales: { total: 0, byUserId: {} },
};

const listeners = new Set();
let socketBound = false;
let refreshTimers = { csr: null, sales: null };

function emit() {
  const snapshot = getAssignedUnreadSnapshot();
  listeners.forEach((fn) => {
    try {
      fn(snapshot);
    } catch (_) {}
  });
}

function channelKey(channel) {
  return channel === "sales" ? "sales" : "csr";
}

export function getAssignedUnreadSnapshot() {
  return {
    csr: { total: state.csr.total, byUserId: { ...state.csr.byUserId } },
    sales: { total: state.sales.total, byUserId: { ...state.sales.byUserId } },
  };
}

export function getAssignedUnreadTotal(channel) {
  return state[channelKey(channel)]?.total || 0;
}

export function subscribeAssignedUnread(fn) {
  ensureAssignedUnreadSocketListeners();
  listeners.add(fn);
  fn(getAssignedUnreadSnapshot());
  return () => listeners.delete(fn);
}

export function applyAssignedUnreadEvent(payload = {}) {
  if (!payload.channel) {
    scheduleRefresh("csr");
    scheduleRefresh("sales");
    return;
  }
  const channel = channelKey(payload.channel);
  const bucket = state[channel];
  if (!bucket) return;

  const userId = payload.fromUserId != null ? String(payload.fromUserId) : null;
  const chatOpen = isAdminChatOpen(payload.chatId);

  if (typeof payload.totalUnread === "number") {
    bucket.total = Math.max(0, Number(payload.totalUnread) || 0);
  }

  if (typeof payload.unreadCount === "number" && payload.unreadCount === 0) {
    if (userId) bucket.byUserId[userId] = 0;
    // Clear any user mapped to this chatId if we only have chatId
    if (!userId && payload.chatId != null) {
      // leave byUserId; totalUnread should correct sidebar
    }
    emit();
    if (typeof payload.totalUnread !== "number") scheduleRefresh(channel);
    return;
  }

  if (chatOpen) {
    if (typeof payload.totalUnread === "number") emit();
    return;
  }

  if (typeof payload.delta === "number" && payload.delta !== 0) {
    if (typeof payload.totalUnread !== "number") {
      bucket.total = Math.max(0, bucket.total + payload.delta);
    }
    if (userId) {
      bucket.byUserId[userId] = Math.max(
        0,
        (Number(bucket.byUserId[userId]) || 0) + payload.delta
      );
    }
  }

  emit();
  if (typeof payload.totalUnread !== "number") scheduleRefresh(channel);
}

function scheduleRefresh(channel) {
  const key = channelKey(channel);
  if (refreshTimers[key]) clearTimeout(refreshTimers[key]);
  refreshTimers[key] = setTimeout(() => {
    refreshAssignedUnreadTotal(key);
  }, 400);
}

export async function refreshAssignedUnreadTotal(channel) {
  const key = channelKey(channel);
  try {
    const data =
      key === "sales"
        ? await salesService.getAssignedUnreadTotal()
        : await csrOutreachService.getAssignedUnreadTotal();
    state[key].total = Number(data?.totalUnread) || 0;
    emit();
  } catch {
    // keep last
  }
}

export function clearUserUnreadLocal(channel, userId) {
  const key = channelKey(channel);
  const uid = String(userId);
  const prev = Number(state[key].byUserId[uid]) || 0;
  state[key].byUserId[uid] = 0;
  if (prev > 0) {
    state[key].total = Math.max(0, state[key].total - prev);
  }
  emit();
  scheduleRefresh(key);
  return prev;
}

export function seedUserUnreadMap(channel, customers = [], totalUnread = null) {
  const key = channelKey(channel);
  const map = {};
  let sum = 0;
  (customers || []).forEach((c) => {
    if (c?.userId == null) return;
    const n = Number(c.unreadCount) || 0;
    map[String(c.userId)] = n;
    sum += n;
  });
  state[key].byUserId = map;
  state[key].total =
    totalUnread != null ? Math.max(0, Number(totalUnread) || 0) : sum;
  emit();
}

export function ensureAssignedUnreadSocketListeners() {
  if (socketBound || typeof window === "undefined") return;
  socketBound = true;
  const socket = getSupportSocket();

  socket.on("assignedChatUnreadUpdated", (payload = {}) => {
    applyAssignedUnreadEvent(payload);
  });
  socket.on("unseenCountUpdated", () => {
    scheduleRefresh("csr");
    scheduleRefresh("sales");
  });
  socket.on("unseenCountsUpdated", () => {
    scheduleRefresh("csr");
    scheduleRefresh("sales");
  });
}
